<script setup>
import { reactive } from 'vue';

const props = defineProps({
  record: { type: Object, required: true },
  labels: { type: Object, required: true },
  busy: Boolean,
});
const emit = defineEmits(['save', 'cancel']);
const firstSource = props.record.source_references?.[0] || {};
const draft = reactive({
  title: props.record.title,
  question: props.record.question,
  answer: props.record.answer,
  record_type: ['fact', 'faq', 'procedure', 'policy'].includes(
    props.record.record_type
  )
    ? props.record.record_type
    : 'faq',
  conditions: props.record.conditions.join('\n'),
  exceptions: props.record.exceptions.join('\n'),
  effective_from: props.record.effective_from || '',
  valid_through: props.record.valid_through || '',
  sourceUrl: firstSource.url_or_file || '',
  sourceExcerpt: firstSource.supporting_excerpt || '',
  sourceLocation: firstSource.location || '',
  verified: false,
  note: '',
});
const fields = [
  'title',
  'question',
  'answer',
  'conditions',
  'exceptions',
  'sourceUrl',
  'sourceExcerpt',
  'sourceLocation',
  'note',
];
const fieldLabel = field =>
  props.labels[field === 'title' ? 'recordTitle' : field];
</script>

<template>
  <form
    class="flex flex-col gap-4 p-6 border rounded-xl bg-n-solid-1 border-n-weak"
    @submit.prevent="emit('save', { ...draft })"
  >
    <h2 class="text-lg font-semibold">{{ labels.revise }}</h2>
    <label
      v-for="field in fields"
      :key="field"
      class="flex flex-col gap-2 text-sm"
    >
      {{ fieldLabel(field) }}
      <textarea
        v-if="
          ['answer', 'conditions', 'exceptions', 'sourceExcerpt'].includes(
            field
          )
        "
        v-model="draft[field]"
        :required="['answer', 'sourceExcerpt'].includes(field)"
        rows="4"
        class="p-3 border rounded-lg bg-n-solid-1 border-n-weak"
      />
      <input
        v-else
        v-model="draft[field]"
        :type="field === 'sourceUrl' ? 'url' : 'text'"
        :required="
          ['title', 'sourceUrl', 'sourceExcerpt', 'note'].includes(field)
        "
        class="p-3 border rounded-lg bg-n-solid-1 border-n-weak"
      />
    </label>
    <label class="flex flex-col gap-2 text-sm">
      <span>{{ labels.recordType }}</span>
      <select
        v-model="draft.record_type"
        class="p-3 border rounded-lg bg-n-solid-1 border-n-weak"
      >
        <option
          v-for="type in ['fact', 'faq', 'procedure', 'policy']"
          :key="type"
          :value="type"
        >
          {{ type }}
        </option>
      </select>
    </label>
    <div class="grid grid-cols-2 gap-3">
      <label class="flex flex-col gap-2 text-sm">
        <span>{{ labels.validFrom }}</span>
        <input v-model="draft.effective_from" type="date" />
      </label>
      <label class="flex flex-col gap-2 text-sm">
        <span>{{ labels.validThrough }}</span>
        <input v-model="draft.valid_through" type="date" />
      </label>
    </div>
    <label class="flex gap-2 text-sm">
      <input v-model="draft.verified" type="checkbox" />
      <span>{{ labels.verified }}</span>
    </label>
    <div class="flex gap-2">
      <button
        type="submit"
        class="px-4 py-2 text-sm rounded-lg bg-n-brand text-white disabled:opacity-40"
        :disabled="busy"
      >
        {{ labels.save }}
      </button>
      <button
        type="button"
        class="px-4 py-2 text-sm border rounded-lg border-n-weak"
        @click="emit('cancel')"
      >
        {{ labels.cancel }}
      </button>
    </div>
  </form>
</template>
